import { Box, Checkbox, Modal, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import Input from "../Common/Input";
import Button from "../Common/Button";
import Pagination from "../Common/Pagination";
import noImageFrom from "../../assets/images/noimage.png";
import { closeDialogue } from "../../util/Dialogue";
import {
  getAllOrganizations,
  updateCourseAccess,
} from "../../store/course/course.action";
import { getOrganizations } from "../../store/organization/organization.action";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: "13px",
  border: "1px solid #C9C9C9",
  boxShadow: 24,
  p: "19px",
  width: "500px",
  maxWidth: "95vw",
};

const ManageCourseAccess = ({ courseId, currentAccessTo = [] }) => {
  const dispatch = useDispatch();
  const { tokenData } = useSelector((state) => state.loggedUser);
  const { dialogue } = useSelector((state) => state.dialogue);
  const {
    selectedCourse: { organizationId },
  } = useSelector((state) => state.course);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [totalOrganizations, setTotalOrganizations] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);

  const currentAccessIds = currentAccessTo.map((org) => org._id);

  useEffect(() => {
    getAllOrganizations(
      { page: 1, limit: 100, searchText },
      tokenData?.isOrg ? "org" : "admin",
    )
      .then((res) => {
        setOrganizations(
          res.organizations.filter((org) => org._id !== organizationId),
        );
        setTotalOrganizations(res.total);
      })
      .catch(() => {
        setOrganizations([]);
        setTotalOrganizations(0);
      });
  }, [searchText, organizationId, tokenData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(debouncedSearchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [debouncedSearchText]);

  const closePopup = () => {
    setSearchText("");
    setDebouncedSearchText("");
    setSelectedIds([]);
    closeDialogue(dispatch);
  };

  const toggleSelection = (orgId) => {
    setSelectedIds((prev) =>
      prev.includes(orgId)
        ? prev.filter((id) => id !== orgId)
        : [...prev, orgId],
    );
  };

  const handleSubmit = () => {
    if (!selectedIds.length) return;

    const mergedIds = [...new Set([...currentAccessIds, ...selectedIds])];
    dispatch(
      updateCourseAccess(
        courseId,
        mergedIds,
        tokenData?.isOrg ? "org" : "admin",
      ),
    );
    closePopup();
  };

  return (
    <Modal
      open={dialogue}
      onClose={closePopup}
      aria-labelledby="manage-course-access-title"
    >
      <Box sx={style} className="create-modal">
        <Typography id="manage-course-access-title" variant="h6" component="h2">
          Add Organizations
        </Typography>

        <div className="mt-3">
          <Input
            classNames={{ root: "w-100" }}
            type="search"
            placeholder="Search organizations..."
            value={debouncedSearchText}
            onChange={(e) => setDebouncedSearchText(e.target.value)}
          />
        </div>

        <div className="mt-3" style={{ maxHeight: "320px", overflowY: "auto" }}>
          {organizations.length ? (
            organizations.map((org) => {
              const alreadyHasAccess = currentAccessIds.includes(org._id);
              return (
                <div
                  key={org._id}
                  className="d-flex align-items-center gap-2 py-2 border-bottom"
                >
                  <Checkbox
                    checked={alreadyHasAccess || selectedIds.includes(org._id)}
                    disabled={alreadyHasAccess}
                    onChange={() => toggleSelection(org._id)}
                  />
                  <img
                    src={org.logo}
                    alt={org.name}
                    width={32}
                    height={32}
                    style={{ borderRadius: "50%", objectFit: "cover" }}
                    onError={(e) => (e.target.src = noImageFrom)}
                  />
                  <span>{org.name}</span>
                  {alreadyHasAccess && (
                    <span
                      className="text-muted ms-auto"
                      style={{ fontSize: "12px" }}
                    >
                      Already has access
                    </span>
                  )}
                </div>
              );
            })
          ) : (
            <p className="text-muted text-center py-3">
              No organizations found
            </p>
          )}
        </div>

        <div className="mt-3 d-flex justify-content-end gap-2">
          <Button type="button" className="close" onClick={closePopup}>
            Cancel
          </Button>
          <Button
            type="button"
            className="submit"
            disabled={!selectedIds.length}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default ManageCourseAccess;
